import { useCallback, useRef, useState } from 'react';

import { useQueryClient } from '@tanstack/react-query';
import {
  Activity,
  AlertCircle,
  AlertTriangle,
  CheckCircle,
  ChevronDown,
  ChevronUp,
  Clock,
  CreditCard,
  Download,
  ExternalLink,
  FileCheck,
  History as HistoryIcon,
  Loader2,
  MessageSquare,
  MousePointerClick,
  Search,
  Wrench,
} from 'lucide-react';
import { Link, useNavigate, useParams } from 'react-router-dom';

import { CallReviewCard } from '@/components/reviews/CallReviewCard';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '@/components/ui/collapsible';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { useToast } from '@/hooks/use-toast';
import { useCall, useCallTracking, useWorkflowLog } from '@/hooks/useCalls';
import { usePageTitle } from '@/hooks/usePageTitle';
import { usePaymentsByCall } from '@/hooks/usePayments';
import { useCreatePipelineRun } from '@/hooks/usePipelineRuns';
import { useReviewConfigs } from '@/hooks/useReviewConfigs';
import { useCallReviews, useStartCallReview } from '@/hooks/useReviews';
import { useExecuteWorkflow, useWorkflows } from '@/hooks/useWorkflows';
import { apiClient } from '@/lib/api/axios';
import { reviewsApi } from '@/lib/api/reviews.api';
import { extractErrorMessage } from '@/lib/utils';
import { useAuthStore } from '@/stores/auth.store';
import { useFeaturesStore } from '@/stores/features.store';
import { formatDateTime, formatSnakeCase } from '@/utils/format';

import { AgentResultsPanel } from './components/AgentResultsPanel';
import { CallBookingsSection } from './components/CallBookingsSection';
import { CallComments } from './components/CallComments';
import { CallDetailHeader } from './components/CallDetailHeader';
import { CallMetadataCard } from './components/CallMetadataCard';
import { HumanReviewForm } from './components/HumanReviewForm';
import { JudgeCorrectionDialog } from './components/JudgeCorrectionDialog';
import { JudgeTuningInlineStatus } from './components/JudgeTuningInlineStatus';
import { LiveCallMonitorCard } from './components/LiveCallMonitorCard';
import { OutcomeDataSection } from './components/OutcomeDataSection';
import { RecordingPlayer } from './components/RecordingPlayer';
import { ReplayAsTestDialog } from './components/ReplayAsTestDialog';
import { TranscriptSection } from './components/TranscriptSection';

import type { SearchQuery } from '@repo/shared-types';

// Base URL for vehicle listings (same as backend redirect controller)
const VEHICLE_LISTING_BASE_URL = 'https://www.v12sportsandclassics.co.uk';

const normalizeVehicleUrl = (url: string): string => {
  if (url.startsWith('/')) {
    return VEHICLE_LISTING_BASE_URL + url;
  }
  return url;
};

type VoiceReviewData = {
  agent_performance?: {
    quality?: unknown;
    reason?: unknown;
  };
  agent_pauses_and_silence?: unknown;
};

type VoicePauseInsight = {
  timeSeconds: number;
  timeLabel: string;
};

const getVoiceReviewData = (
  additionalReviewData?: Record<string, unknown>,
): VoiceReviewData | null => {
  if (!additionalReviewData || typeof additionalReviewData !== 'object') {
    return null;
  }

  const voiceReview = additionalReviewData.voice_review;
  if (!voiceReview || typeof voiceReview !== 'object') {
    return null;
  }

  return voiceReview as VoiceReviewData;
};

const parseTimestampToSeconds = (rawValue: unknown): number | null => {
  if (typeof rawValue === 'number' && Number.isFinite(rawValue) && rawValue >= 0) {
    return rawValue;
  }

  if (typeof rawValue !== 'string') {
    return null;
  }

  const value = rawValue.trim();
  if (!value) {
    return null;
  }

  const directNumber = Number(value);
  if (Number.isFinite(directNumber) && directNumber >= 0) {
    return directNumber;
  }

  const parts = value.split(':').map((part) => Number(part));
  if (parts.some((part) => !Number.isFinite(part) || part < 0)) {
    return null;
  }

  if (parts.length === 2) {
    return parts[0] * 60 + parts[1];
  }

  if (parts.length === 3) {
    return parts[0] * 3600 + parts[1] * 60 + parts[2];
  }

  return null;
};

const formatSecondsAsTimestamp = (seconds: number): string => {
  const safeSeconds = Math.max(0, Math.floor(seconds));
  const hours = Math.floor(safeSeconds / 3600);
  const minutes = Math.floor((safeSeconds % 3600) / 60);
  const remainingSeconds = safeSeconds % 60;

  if (hours > 0) {
    return `${String(hours).padStart(2, '0')}:${String(minutes).padStart(2, '0')}:${String(remainingSeconds).padStart(2, '0')}`;
  }

  return `${String(minutes).padStart(2, '0')}:${String(remainingSeconds).padStart(2, '0')}`;
};

const parseVoicePauseInsights = (rawPauses: unknown): VoicePauseInsight[] => {
  if (!Array.isArray(rawPauses)) {
    return [];
  }

  return rawPauses
    .map((pause) => {
      if (!pause || typeof pause !== 'object') {
        return null;
      }

      const parsedSeconds = parseTimestampToSeconds((pause as { time?: unknown }).time);
      if (parsedSeconds === null) {
        return null;
      }

      return {
        timeSeconds: parsedSeconds,
        timeLabel: formatSecondsAsTimestamp(parsedSeconds),
      };
    })
    .filter((pause): pause is VoicePauseInsight => !!pause);
};

/** Extract boolean fields from a completed call review for the correction dialog. */
const extractReviewLabels = (review: {
  outcome_matches?: boolean | null;
  availability_matches?: boolean | null;
  lead_data_matches?: boolean | null;
  additional_review_data?: Record<string, unknown> | null;
}): Record<string, boolean> => {
  const labels: Record<string, boolean> = {};
  if (typeof review.outcome_matches === 'boolean') labels.outcome_matches = review.outcome_matches;
  if (typeof review.availability_matches === 'boolean')
    labels.availability_matches = review.availability_matches;
  if (typeof review.lead_data_matches === 'boolean')
    labels.lead_data_matches = review.lead_data_matches;
  // Pick booleans from additional_review_data.
  if (review.additional_review_data && typeof review.additional_review_data === 'object') {
    for (const [key, value] of Object.entries(review.additional_review_data)) {
      if (typeof value === 'boolean' && key !== 'two_turn_skipped') labels[key] = value;
    }
  }
  return labels;
};

/** Extract non-boolean fields from a completed call review for the correction dialog. */
const extractReviewData = (review: {
  reviewed_outcome?: string | null;
  reviewed_availability?: unknown;
  reviewed_lead_data?: unknown;
  additional_review_data?: Record<string, unknown> | null;
}): Record<string, unknown> => {
  const data: Record<string, unknown> = {};
  if (review.reviewed_outcome) data.reviewed_outcome = review.reviewed_outcome;
  if (review.reviewed_availability) data.reviewed_availability = review.reviewed_availability;
  if (review.reviewed_lead_data) data.reviewed_lead_data = review.reviewed_lead_data;
  // Pick non-booleans from additional_review_data.
  if (review.additional_review_data && typeof review.additional_review_data === 'object') {
    for (const [key, value] of Object.entries(review.additional_review_data)) {
      if (
        typeof value !== 'boolean' &&
        key !== 'voice_review' &&
        key !== 'transcript_divergence' &&
        key !== 'two_turn_skipped'
      ) {
        data[key] = value;
      }
    }
  }
  return data;
};

export const CallDetailPage = () => {
  usePageTitle('Call Details');
  const queryClient = useQueryClient();
  const { callId } = useParams<{ callId: string }>();
  const { data: call, isLoading } = useCall(callId);
  const { data: tracking } = useCallTracking(callId);
  // Clients view: read-only page — internal tooling (reviews, workflows,
  // payments, comments, agent runs) is hidden and its endpoints are off-limits.
  const isClientView = useAuthStore((s) => s.user?.role === 'client');
  const paymentLinksEnabled = useFeaturesStore((s) => s.paymentLinks);
  const bookingsEnabled = useFeaturesStore((s) => s.bookings);
  const featureAgentImprovement = useFeaturesStore((s) => s.agentImprovement);
  const { data: paymentLinks } = usePaymentsByCall(
    paymentLinksEnabled && !isClientView ? callId : undefined,
  );
  const { data: reviews, isLoading: reviewsLoading } = useCallReviews(
    isClientView ? undefined : callId,
  );

  const startReviewMutation = useStartCallReview();
  const { data: reviewConfigs = [] } = useReviewConfigs({ enabled: !isClientView });
  const { toast } = useToast();
  const [configDialogOpen, setConfigDialogOpen] = useState(false);
  const [selectedConfigId, setSelectedConfigId] = useState<string>('');
  const [isPromptExpanded, setIsPromptExpanded] = useState(false);

  const { data: workflowLog } = useWorkflowLog(isClientView ? undefined : callId);
  const executeWorkflow = useExecuteWorkflow();
  const { data: availableWorkflows = [] } = useWorkflows({ enabled: !isClientView });
  const [selectedWorkflowId, setSelectedWorkflowId] = useState<string>('');
  const [workflowRunning, setWorkflowRunning] = useState(false);
  const audioPlayerRef = useRef<HTMLAudioElement | null>(null);
  const [expandedQueryId, setExpandedQueryId] = useState<string | null>(null);
  const [apiNodeDetail, setApiNodeDetail] = useState<{
    nodeId: string;
    request_payload?: unknown;
    response_data?: unknown;
  } | null>(null);
  const [reviewCorrectionOpen, setReviewCorrectionOpen] = useState(false);
  const [replayOpen, setReplayOpen] = useState(false);
  const createPipelineRun = useCreatePipelineRun();
  const navigate = useNavigate();

  // Quality flags live in `quality.flags` (booleans); severity lives in `quality.data`.
  const qualitySeverity = String(call?.quality?.data?.severity ?? '').toLowerCase();
  const isFlaggedCall =
    featureAgentImprovement &&
    !call?.is_test &&
    (call?.quality?.flags?.is_failure === true ||
      qualitySeverity === 'critical' ||
      qualitySeverity === 'major');

  const handleFixAndVerify = useCallback(async () => {
    if (!call?.configuration_id) return;
    try {
      const run = await createPipelineRun.mutateAsync({
        configurationId: call.configuration_id,
        callIds: [call.id],
      });
      navigate(`/agent-improvement/runs/${run.id}`);
    } catch {
      toast({ variant: 'destructive', title: 'Error', description: 'Failed to start run.' });
    }
  }, [call?.configuration_id, call?.id, createPipelineRun, navigate, toast]);

  const latestReview = reviews?.[0];
  const latestCompletedReview = reviews?.find((review) => review.review_status === 'COMPLETED');
  const reviewForInsights = latestCompletedReview ?? latestReview;
  const voiceReviewData = getVoiceReviewData(reviewForInsights?.additional_review_data);
  const voicePauseInsights = parseVoicePauseInsights(voiceReviewData?.agent_pauses_and_silence);
  const voicePerformanceQuality =
    typeof voiceReviewData?.agent_performance?.quality === 'string' &&
    voiceReviewData.agent_performance.quality.trim().length > 0
      ? voiceReviewData.agent_performance.quality
      : 'unknown';
  const voicePerformanceReason =
    typeof voiceReviewData?.agent_performance?.reason === 'string' &&
    voiceReviewData.agent_performance.reason.trim().length > 0
      ? voiceReviewData.agent_performance.reason
      : 'No voice performance reason provided.';

  const handleSetAudioRef = useCallback((el: HTMLAudioElement | null) => {
    audioPlayerRef.current = el;
  }, []);

  const handleSeekToPause = (time: number) => {
    const audioElement = audioPlayerRef.current;
    if (!audioElement) {
      toast({
        variant: 'destructive',
        title: 'Audio Player Not Ready',
        description: 'Load call audio first, then click a pause timestamp.',
      });
      return;
    }

    const seekTarget = Math.max(0, time - 2);
    audioElement.currentTime = seekTarget;
    audioElement.play().catch(() => {
      // Browser autoplay policies can block programmatic play until user interacts.
    });
  };

  const isReviewed = call?.is_reviewed ?? false;

  const handleStartReview = async () => {
    if (!call || !callId) return;

    if (call.status !== 'COMPLETED') {
      toast({
        variant: 'destructive',
        title: 'Cannot review',
        description: 'Only completed calls can be reviewed.',
      });
      return;
    }

    setSelectedConfigId('');
    setConfigDialogOpen(true);
  };

  const handleConfirmStartReview = async () => {
    if (!callId) return;
    setConfigDialogOpen(false);

    try {
      await startReviewMutation.mutateAsync({
        callId,
        reviewConfigId: selectedConfigId || undefined,
      });
      toast({
        variant: 'success',
        title: 'Review started',
        description: 'AI review has been initiated for this call.',
      });
    } catch (error: unknown) {
      const err = error as { response?: { data?: { message?: string } } };
      toast({
        variant: 'destructive',
        title: 'Failed to start review',
        description: err.response?.data?.message || 'An error occurred.',
      });
    }
  };

  const handleDownloadRecording = async () => {
    if (!call || !callId) return;

    try {
      const pathParts = call.recording_path!.split('/');
      const filename = pathParts[pathParts.length - 1];

      const response = await apiClient.get(`/storage/recordings/${callId}/download`, {
        responseType: 'blob',
      });

      const blob = response.data;
      const downloadUrl = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = downloadUrl;
      link.download = filename || 'recording.wav';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(downloadUrl);
    } catch {
      toast({
        variant: 'destructive',
        title: 'Download Failed',
        description: 'Failed to download recording. Please try again.',
      });
    }
  };

  const handleOpenRecording = async () => {
    if (!callId) return;

    try {
      const response = await apiClient.get<{ url: string }>(`/storage/recordings/${callId}`);
      window.open(response.data.url, '_blank');
    } catch {
      toast({
        variant: 'destructive',
        title: 'Open Failed',
        description: 'Failed to open recording. Please try again.',
      });
    }
  };

  const handleDownloadInternalRecording = async () => {
    if (!call || !callId) return;

    try {
      const pathParts = call.internal_recording_path!.split('/');
      const filename = pathParts[pathParts.length - 1];

      const response = await apiClient.get(`/storage/recordings/${callId}/internal/download`, {
        responseType: 'blob',
      });

      const blob = response.data;
      const downloadUrl = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = downloadUrl;
      link.download = filename || 'internal-recording.wav';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(downloadUrl);
    } catch {
      toast({
        variant: 'destructive',
        title: 'Download Failed',
        description: 'Failed to download internal recording. Please try again.',
      });
    }
  };

  const handleOpenInternalRecording = async () => {
    if (!callId) return;

    try {
      const response = await apiClient.get<{ url: string }>(
        `/storage/recordings/${callId}/internal`,
      );
      window.open(response.data.url, '_blank');
    } catch {
      toast({
        variant: 'destructive',
        title: 'Open Failed',
        description: 'Failed to open internal recording. Please try again.',
      });
    }
  };

  const handleRunWorkflow = async (dryRun: boolean) => {
    if (!callId) return;
    try {
      setWorkflowRunning(true);
      await executeWorkflow.mutateAsync({
        callId,
        workflowId: selectedWorkflowId || undefined,
        dryRun,
      });
      toast({
        variant: 'success',
        title: dryRun ? 'Dry run started' : 'Workflow started',
        description: 'Running in background. The page will refresh when complete.',
      });
      let attempts = 0;
      const maxAttempts = 40;
      const pollInterval = setInterval(async () => {
        attempts++;
        await queryClient.invalidateQueries({ queryKey: ['calls', callId] });
        await queryClient.invalidateQueries({ queryKey: ['call-reviews', callId] });
        if (attempts >= maxAttempts) {
          clearInterval(pollInterval);
          setWorkflowRunning(false);
          toast({
            variant: 'default',
            title: 'Workflow may still be running',
            description: 'Refresh the page to check the latest status.',
          });
        }
      }, 3000);
      setTimeout(() => {
        clearInterval(pollInterval);
        setWorkflowRunning(false);
        queryClient.invalidateQueries({ queryKey: ['call', callId] });
        queryClient.invalidateQueries({ queryKey: ['call-reviews', callId] });
      }, 120000);
    } catch (err) {
      setWorkflowRunning(false);
      toast({
        variant: 'destructive',
        title: 'Workflow execution failed',
        description: extractErrorMessage(err),
      });
    }
  };

  const handleTranslateTranscript = async () => {
    if (!callId) return;
    try {
      const result = await reviewsApi.translateCallTranscript(callId);
      if (result.status === 'not_needed') {
        toast({
          title: 'No translation needed',
          description: `The transcript is already in the target language (${result.targetLanguage.toUpperCase()}).`,
        });
      }
      await queryClient.invalidateQueries({ queryKey: ['calls', callId] });
    } catch (err) {
      toast({
        variant: 'destructive',
        title: 'Translation failed',
        description: extractErrorMessage(err),
      });
    }
  };

  if (isLoading) {
    return (
      <div className="space-y-6">
        <div className="flex items-center justify-between">
          <div>
            <p className="text-muted-foreground text-sm tracking-[0.3em] uppercase">Call detail</p>
            <h1 className="text-3xl font-bold tracking-tight">Loading...</h1>
          </div>
          <Button variant="outline" asChild>
            <Link to="/calls">Back to calls</Link>
          </Button>
        </div>
      </div>
    );
  }

  if (!call) {
    return (
      <div className="space-y-6">
        <div className="flex items-center justify-between">
          <div>
            <p className="text-muted-foreground text-sm tracking-[0.3em] uppercase">Call detail</p>
            <h1 className="text-3xl font-bold tracking-tight">Not Found</h1>
            <p className="text-muted-foreground">We could not locate call ID {callId}.</p>
          </div>
          <Button variant="outline" asChild>
            <Link to="/calls">Back to calls</Link>
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-4 overflow-hidden">
      <CallDetailHeader
        call={call}
        availableWorkflows={availableWorkflows}
        workflowRunning={workflowRunning}
        selectedWorkflowId={selectedWorkflowId}
        onWorkflowIdChange={setSelectedWorkflowId}
        onRunWorkflow={(dryRun) => void handleRunWorkflow(dryRun)}
        onReplay={featureAgentImprovement ? () => setReplayOpen(true) : undefined}
        onFixAndVerify={isFlaggedCall ? () => void handleFixAndVerify() : undefined}
        fixAndVerifyPending={createPipelineRun.isPending}
        readOnly={isClientView}
      />

      {/* Live monitor — top of page for active calls ONLY */}
      {call.status !== 'COMPLETED' && !isClientView && (
        <LiveCallMonitorCard
          callId={call.id}
          status={call.status}
          transport={call.call_transport}
        />
      )}

      {/* Two-column layout */}
      <div className="grid gap-4 lg:grid-cols-5">
        {/* Left column — primary content */}
        <div className="space-y-4 lg:col-span-3">
          {/* Text sessions are one slice of an ongoing chat — the transcript
              below is NOT the whole conversation. Point at the contact view.
              Detection: explicit modality, the text- session sid prefix, or a
              chat transport without any recording (chats never record audio). */}
          {(call.modality === 'TEXT' ||
            call.call_sid?.startsWith('text-') ||
            (['WHATSAPP', 'WEB_WIDGET', 'EMAIL'].includes(call.call_transport ?? '') &&
              !call.recording_path &&
              !call.internal_recording_path)) && (
            <div className="border-primary/20 bg-primary/5 flex flex-wrap items-center justify-between gap-3 rounded-lg border px-4 py-3">
              <div className="flex min-w-0 items-center gap-2 text-sm">
                <MessageSquare className="text-primary h-4 w-4 shrink-0" />
                <span>
                  This is a chat session — the transcript below covers only this session, not the
                  contact&apos;s full conversation.
                </span>
              </div>
              {call.contact_id && !isClientView && (
                <Button asChild size="sm" className="shrink-0">
                  <Link to={`/sales-console/${call.contact_id}`}>
                    <ExternalLink className="mr-1.5 h-3.5 w-3.5" />
                    Open full conversation
                  </Link>
                </Button>
              )}
            </div>
          )}

          <TranscriptSection
            transcript={call.transcript}
            enhancedTranscript={call.enhanced_transcript}
            originalTranscript={call.original_transcript ?? call.transcript}
            hasEnhancedTranscriptFlag={call.has_enhanced_transcript}
            translatedTranscript={call.translated_transcript}
            translationLanguage={call.translation_language}
            detectedSourceLanguage={call.detected_source_language}
            onTranslate={isClientView ? undefined : handleTranslateTranscript}
          />

          {/* Audio player card */}
          <RecordingPlayer
            callId={call.id}
            recordingPath={call.recording_path}
            internalRecordingPath={call.internal_recording_path}
            onAudioRef={handleSetAudioRef}
          >
            <div className="flex flex-wrap gap-2">
              {call.recording_path && (
                <>
                  <Button variant="outline" size="sm" onClick={handleDownloadRecording}>
                    <Download className="mr-2 h-4 w-4" />
                    Download Provider
                  </Button>
                  <Button variant="outline" size="sm" onClick={handleOpenRecording}>
                    <ExternalLink className="mr-2 h-4 w-4" />
                    Open Provider
                  </Button>
                </>
              )}
              {call.internal_recording_path && (
                <>
                  <Button variant="outline" size="sm" onClick={handleDownloadInternalRecording}>
                    <Download className="mr-2 h-4 w-4" />
                    Download Internal
                  </Button>
                  <Button variant="outline" size="sm" onClick={handleOpenInternalRecording}>
                    <ExternalLink className="mr-2 h-4 w-4" />
                    Open Internal
                  </Button>
                </>
              )}
            </div>
          </RecordingPlayer>

          {!isClientView && <AgentResultsPanel call={call} />}
        </div>

        {/* Right column */}
        <div className="space-y-4 lg:col-span-2">
          <CallMetadataCard call={call} readOnly={isClientView} />
          <OutcomeDataSection call={call} isReviewed={isReviewed} />
          {!isClientView && <HumanReviewForm callId={callId!} call={call} />}
          {!isClientView && (
            <CallComments callId={call.id} comments={call.comments} isLoading={isLoading} />
          )}
          {bookingsEnabled && !isClientView && <CallBookingsSection callId={call.id} />}
        </div>
      </div>

      {/* Collapsible full-width sections */}

      {/* Configuration — collapsed by default */}
      <Collapsible defaultOpen={false}>
        <Card>
          <CardHeader className="pb-2">
            <CollapsibleTrigger className="flex w-full cursor-pointer items-center justify-between select-none">
              <div className="flex items-center gap-2">
                <HistoryIcon className="text-muted-foreground h-4 w-4" />
                <CardTitle className="text-sm">Configuration</CardTitle>
              </div>
              <ChevronDown className="text-muted-foreground h-4 w-4 transition-transform [[data-state=open]>&]:rotate-180" />
            </CollapsibleTrigger>
          </CardHeader>
          <CollapsibleContent>
            <CardContent>
              <div className="space-y-4">
                <div>
                  <p className="text-sm font-semibold">
                    {call.configuration?.name || 'Legacy Configuration'}
                  </p>
                  <p className="text-muted-foreground mt-0.5 text-xs break-all">
                    ID: {call.configuration_id || 'N/A'}
                  </p>
                </div>

                {call.configuration_version ? (
                  <div className="bg-muted/30 space-y-2 rounded-md border p-3">
                    <p className="text-muted-foreground text-[10px] font-bold tracking-wider uppercase">
                      Prompt Template
                    </p>
                    <div className="relative">
                      <div
                        className={`overflow-hidden font-mono text-xs whitespace-pre-wrap transition-all duration-200 ${
                          isPromptExpanded ? 'max-h-none' : 'max-h-[80px]'
                        }`}
                      >
                        {call.configuration_version.prompt_template}
                      </div>
                      {!isPromptExpanded &&
                        call.configuration_version.prompt_template.split('\n').length > 4 && (
                          <div className="from-muted/50 absolute right-0 bottom-0 left-0 flex h-8 items-end justify-center bg-gradient-to-t to-transparent">
                            <Button
                              variant="ghost"
                              size="sm"
                              className="bg-background/80 h-6 text-[10px] shadow-sm backdrop-blur-sm"
                              onClick={() => setIsPromptExpanded(true)}
                            >
                              Read More <ChevronDown className="ml-1 h-3 w-3" />
                            </Button>
                          </div>
                        )}
                      {isPromptExpanded && (
                        <div className="mt-2 flex justify-center">
                          <Button
                            variant="ghost"
                            size="sm"
                            className="h-6 text-[10px]"
                            onClick={() => setIsPromptExpanded(false)}
                          >
                            Show Less <ChevronUp className="ml-1 h-3 w-3" />
                          </Button>
                        </div>
                      )}
                    </div>
                  </div>
                ) : (
                  <div className="bg-destructive/5 border-destructive/10 text-destructive flex items-center gap-2 rounded-md border p-3 text-xs">
                    <AlertTriangle className="h-4 w-4" />
                    The specific prompt version used for this call has been deleted from settings.
                  </div>
                )}
              </div>
            </CardContent>
          </CollapsibleContent>
        </Card>
      </Collapsible>

      {/* AI Review — collapsed by default */}
      {call.status === 'COMPLETED' && !isClientView && (
        <Collapsible defaultOpen={false}>
          <Card>
            <CardHeader className="pb-2">
              <CollapsibleTrigger className="flex w-full cursor-pointer items-center justify-between select-none">
                <div className="flex items-center gap-2">
                  <FileCheck className="text-muted-foreground h-4 w-4" />
                  <CardTitle className="text-sm">AI Review</CardTitle>
                  {latestReview && (
                    <>
                      {latestReview.review_status === 'PENDING' && (
                        <Badge variant="outline" className="text-yellow-700">
                          <Clock className="mr-1 h-3 w-3" />
                          Pending
                        </Badge>
                      )}
                      {latestReview.review_status === 'IN_PROGRESS' && (
                        <Badge variant="outline" className="text-blue-700">
                          <Loader2 className="mr-1 h-3 w-3 animate-spin" />
                          In Progress
                        </Badge>
                      )}
                      {latestReview.review_status === 'COMPLETED' && (
                        <Badge variant="outline" className="text-green-700">
                          <CheckCircle className="mr-1 h-3 w-3" />
                          Completed
                        </Badge>
                      )}
                      {latestReview.review_status === 'FAILED' && (
                        <Badge variant="outline" className="text-red-700">
                          <AlertCircle className="mr-1 h-3 w-3" />
                          Failed
                        </Badge>
                      )}
                    </>
                  )}
                </div>
                <ChevronDown className="text-muted-foreground h-4 w-4 transition-transform [[data-state=open]>&]:rotate-180" />
              </CollapsibleTrigger>
            </CardHeader>
            <CollapsibleContent>
              <CardContent>
                <div className="space-y-4">
                  <div className="flex flex-wrap gap-2">
                    {!reviewsLoading && !latestReview && (
                      <Button
                        size="sm"
                        onClick={handleStartReview}
                        disabled={startReviewMutation.isPending}
                      >
                        Start Review
                      </Button>
                    )}
                    {latestReview?.review_status === 'COMPLETED' && (
                      <>
                        <Button size="sm" variant="default" asChild>
                          <Link to={`/reviews/call/${callId}`}>View All Reviews</Link>
                        </Button>
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={handleStartReview}
                          disabled={startReviewMutation.isPending}
                        >
                          <FileCheck className="mr-2 h-4 w-4" />
                          Start New Review
                        </Button>
                      </>
                    )}
                    {latestReview?.review_status === 'FAILED' && (
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={handleStartReview}
                        disabled={startReviewMutation.isPending}
                      >
                        <FileCheck className="mr-2 h-4 w-4" />
                        Retry Review
                      </Button>
                    )}
                  </div>
                  {reviewsLoading ? (
                    <div className="flex items-center justify-center py-4">
                      <div className="text-center">
                        <div className="border-primary mx-auto h-6 w-6 animate-spin rounded-full border-b-2" />
                        <p className="text-muted-foreground mt-2 text-sm">
                          Loading review status...
                        </p>
                      </div>
                    </div>
                  ) : !latestReview ? (
                    <div className="py-6 text-center">No Reviews Yet</div>
                  ) : latestReview.review_status === 'COMPLETED' ? (
                    <div className="space-y-3">
                      {latestReview.confidence_score !== null && (
                        <div>
                          <div className="mb-1 flex items-center justify-between">
                            <p className="text-muted-foreground text-xs">Confidence Score</p>
                            <span className="text-sm font-bold">
                              {((latestReview.confidence_score || 0) * 100).toFixed(0)}%
                            </span>
                          </div>
                          <div className="h-2 flex-1 rounded-full bg-gray-200">
                            <div
                              className="h-2 rounded-full bg-blue-500"
                              style={{
                                width: `${(latestReview.confidence_score || 0) * 100}%`,
                              }}
                            />
                          </div>
                        </div>
                      )}
                      {latestReview.outcome_matches === false && (
                        <div className="flex items-center gap-2 rounded bg-orange-50 p-2 text-sm text-orange-700 dark:bg-orange-950/20 dark:text-orange-400">
                          <AlertCircle className="h-4 w-4" />
                          <span>Outcome discrepancy detected</span>
                        </div>
                      )}
                      {latestReview.reviewed_at && (
                        <p className="text-muted-foreground text-xs">
                          Reviewed: {formatDateTime(latestReview.reviewed_at)}
                        </p>
                      )}
                    </div>
                  ) : latestReview.review_status === 'IN_PROGRESS' ||
                    latestReview.review_status === 'PENDING' ? (
                    <div className="py-4 text-center">
                      <p className="text-muted-foreground mb-3 text-sm">
                        Review is currently being processed...
                      </p>
                      <Button variant="outline" size="sm" asChild>
                        <Link to={`/reviews/call/${callId}`}>See Progress</Link>
                      </Button>
                    </div>
                  ) : latestReview.review_status === 'FAILED' ? (
                    <div className="py-4 text-center">
                      <p className="mb-3 text-sm text-red-600 dark:text-red-400">
                        Review failed. Please try again.
                      </p>
                    </div>
                  ) : null}

                  {latestCompletedReview && latestCompletedReview.review_config_id && (
                    <>
                      <Button
                        size="sm"
                        variant="ghost"
                        className="text-muted-foreground mt-2 h-7 text-xs"
                        onClick={() => setReviewCorrectionOpen(true)}
                      >
                        <AlertCircle className="mr-1 h-3 w-3" />
                        Judge got this wrong
                      </Button>

                      <JudgeTuningInlineStatus callId={callId!} targetType="review" />

                      <JudgeCorrectionDialog
                        open={reviewCorrectionOpen}
                        onOpenChange={setReviewCorrectionOpen}
                        callId={callId!}
                        targetType="review"
                        configId={latestCompletedReview.review_config_id}
                        configName={
                          reviewConfigs.find((c) => c.id === latestCompletedReview.review_config_id)
                            ?.name ?? 'Review'
                        }
                        actualLabels={extractReviewLabels(latestCompletedReview)}
                        actualData={extractReviewData(latestCompletedReview)}
                      />
                    </>
                  )}
                </div>
              </CardContent>
            </CollapsibleContent>
          </Card>
        </Collapsible>
      )}

      {/* Review Details — collapsed by default */}
      {latestCompletedReview && (
        <Collapsible defaultOpen={false}>
          <Card>
            <CardHeader className="pb-2">
              <CollapsibleTrigger className="flex w-full cursor-pointer items-center justify-between select-none">
                <CardTitle className="text-sm">Review Details</CardTitle>
                <ChevronDown className="text-muted-foreground h-4 w-4 transition-transform [[data-state=open]>&]:rotate-180" />
              </CollapsibleTrigger>
            </CardHeader>
            <CollapsibleContent>
              <CardContent className="space-y-6">
                <CallReviewCard callReview={latestCompletedReview} compact />

                <div className="space-y-4 rounded-lg border p-4">
                  <div>
                    <p className="text-muted-foreground text-xs font-semibold tracking-widest uppercase">
                      Agent Performance Quality
                    </p>
                    <p className="mt-1 text-sm font-semibold capitalize">
                      {voicePerformanceQuality}
                    </p>
                    <p className="text-muted-foreground mt-1 text-sm">{voicePerformanceReason}</p>
                  </div>

                  <div>
                    <p className="text-muted-foreground mb-2 text-xs font-semibold tracking-widest uppercase">
                      Agent Pauses and Silence
                    </p>
                    {voicePauseInsights.length > 0 ? (
                      <div className="flex flex-wrap gap-2">
                        {voicePauseInsights.map((pause, index) => (
                          <Button
                            key={`${pause.timeSeconds}-${index}`}
                            type="button"
                            variant="outline"
                            size="sm"
                            onClick={() => handleSeekToPause(pause.timeSeconds)}
                          >
                            {pause.timeLabel}
                          </Button>
                        ))}
                      </div>
                    ) : (
                      <p className="text-muted-foreground text-sm">
                        No notable agent pauses detected.
                      </p>
                    )}
                    <p className="text-muted-foreground mt-2 text-xs">
                      Clicking a timestamp seeks playback to 2 seconds before the pause.
                    </p>
                  </div>
                </div>
              </CardContent>
            </CollapsibleContent>
          </Card>
        </Collapsible>
      )}

      {/* Workflow Log — collapsed by default (open if errors) */}
      {workflowLog?.results && workflowLog.results.length > 0 && !isClientView && (
        <Collapsible defaultOpen={workflowLog.errors > 0}>
          <Card>
            <CardHeader className="pb-2">
              <CollapsibleTrigger className="flex w-full cursor-pointer items-center justify-between select-none">
                <div className="flex items-center gap-2">
                  <Activity className="text-muted-foreground h-4 w-4" />
                  <CardTitle className="text-sm">Workflow Execution Log</CardTitle>
                  <span className="text-muted-foreground text-xs">
                    {workflowLog.executed_nodes}/{workflowLog.total_nodes} nodes
                  </span>
                  {workflowLog.errors > 0 && (
                    <Badge variant="destructive" className="text-xs">
                      {workflowLog.errors} failed
                    </Badge>
                  )}
                  {workflowLog.dry_run && (
                    <Badge variant="outline" className="text-xs text-yellow-800">
                      Dry Run
                    </Badge>
                  )}
                </div>
                <ChevronDown className="text-muted-foreground h-4 w-4 transition-transform [[data-state=open]>&]:rotate-180" />
              </CollapsibleTrigger>
            </CardHeader>
            <CollapsibleContent>
              <CardContent>
                <div className="space-y-1.5">
                  {workflowLog.results.map((result, idx) => {
                    const isApiNode = result.nodeType === 'call_api';
                    return (
                      <div
                        key={idx}
                        className={`rounded-md border px-3 py-2 text-sm ${
                          result.status === 'error'
                            ? 'border-red-200 bg-red-50 dark:border-red-800 dark:bg-red-950/20'
                            : ''
                        } ${isApiNode ? 'hover:bg-muted/50 cursor-pointer transition-colors' : ''}`}
                        onClick={
                          isApiNode
                            ? () =>
                                setApiNodeDetail({
                                  nodeId: result.nodeId,
                                  request_payload: result.request_payload,
                                  response_data: result.response_data,
                                })
                            : undefined
                        }
                      >
                        <div className="flex items-center justify-between">
                          <div className="flex items-center gap-2">
                            <span
                              className={`inline-block h-2.5 w-2.5 rounded-full ${
                                result.status === 'success'
                                  ? 'bg-green-500'
                                  : result.status === 'error'
                                    ? 'bg-red-500'
                                    : 'bg-yellow-500'
                              }`}
                            />
                            <span className="font-medium">{result.nodeId}</span>
                            <span className="text-muted-foreground text-xs">
                              ({result.nodeType})
                            </span>
                            {isApiNode && (
                              <ExternalLink className="text-muted-foreground h-3 w-3" />
                            )}
                            {result.branch && (
                              <span
                                className={`rounded px-1.5 py-0.5 text-xs ${
                                  result.branch === 'true'
                                    ? 'bg-green-100 text-green-700 dark:bg-green-900 dark:text-green-300'
                                    : 'bg-red-100 text-red-700 dark:bg-red-900 dark:text-red-300'
                                }`}
                              >
                                {result.branch}
                              </span>
                            )}
                          </div>
                          {result.duration_ms !== undefined && (
                            <span className="text-muted-foreground text-xs">
                              {result.duration_ms}ms
                            </span>
                          )}
                        </div>
                        {result.message && (
                          <div
                            className={`mt-1.5 rounded px-2 py-1.5 text-xs ${
                              result.status === 'error'
                                ? 'bg-red-100 text-red-800 dark:bg-red-900/50 dark:text-red-200'
                                : 'text-muted-foreground bg-muted'
                            }`}
                          >
                            {result.message}
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              </CardContent>
            </CollapsibleContent>
          </Card>
        </Collapsible>
      )}

      {/* Auto Correction Status */}
      {call.auto_corrected && call.auto_correction_data && (
        <Card>
          <CardHeader className="pb-2">
            <div className="flex items-center gap-2">
              <Wrench className="text-muted-foreground h-5 w-5" />
              <CardTitle className="text-sm">Auto-Corrected by AI</CardTitle>
            </div>
            <CardDescription>
              This call&apos;s data was automatically corrected based on AI review
              {call.auto_correction_data.corrected_at && (
                <span className="ml-1">
                  on {formatDateTime(call.auto_correction_data.corrected_at)}
                </span>
              )}
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              {call.auto_correction_data.corrected_fields.map((field: string) => {
                const originalValue = call.auto_correction_data?.original_values?.[field];
                const currentValue =
                  field === 'outcome'
                    ? call.outcome
                    : field === 'availability_data'
                      ? call.availability_data
                      : field === 'lead_data'
                        ? call.lead_data
                        : null;

                const formatValue = (value: unknown): string => {
                  if (value === null || value === undefined) {
                    return 'Value not available';
                  }
                  if (typeof value === 'object') return JSON.stringify(value, null, 2);
                  return String(value);
                };

                const fieldLabel = formatSnakeCase(field);

                return (
                  <div key={field} className="rounded-lg border p-4">
                    <div className="mb-3 flex items-center gap-2">
                      <AlertTriangle className="h-4 w-4 text-amber-600 dark:text-amber-400" />
                      <span className="font-semibold">{fieldLabel}</span>
                    </div>
                    <div className="grid gap-4 md:grid-cols-2">
                      <div className="rounded-md bg-red-50 p-3 dark:bg-red-950/30">
                        <p className="mb-1 text-xs font-medium text-red-700 dark:text-red-400">
                          Original (Before Correction)
                        </p>
                        {field === 'outcome' ? (
                          <span className="inline-block rounded-full bg-red-100 px-2 py-1 text-xs font-medium text-red-800 dark:bg-red-900 dark:text-red-200">
                            {formatValue(originalValue)}
                          </span>
                        ) : (
                          <pre className="max-h-40 overflow-auto text-xs text-red-800 dark:text-red-200">
                            {formatValue(originalValue)}
                          </pre>
                        )}
                      </div>
                      <div className="rounded-md bg-green-50 p-3 dark:bg-green-950/30">
                        <p className="mb-1 text-xs font-medium text-green-700 dark:text-green-400">
                          Corrected (Current Value)
                        </p>
                        {field === 'outcome' ? (
                          <span className="inline-block rounded-full bg-green-100 px-2 py-1 text-xs font-medium text-green-800 dark:bg-green-900 dark:text-green-200">
                            {formatValue(currentValue)}
                          </span>
                        ) : (
                          <pre className="max-h-40 overflow-auto text-xs text-green-800 dark:text-green-200">
                            {formatValue(currentValue)}
                          </pre>
                        )}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Payment Links Card */}
      {paymentLinks && paymentLinks.length > 0 && (
        <Card>
          <CardHeader className="pb-2">
            <div className="flex items-center gap-2">
              <CreditCard className="text-muted-foreground h-5 w-5" />
              <CardTitle className="text-sm">Payment Links</CardTitle>
            </div>
            <CardDescription>Payment links sent to the customer during this call</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              {paymentLinks.map((pl) => (
                <div
                  key={pl.id}
                  className="flex items-center justify-between rounded-lg border p-4 dark:border-gray-700"
                >
                  <div className="space-y-1">
                    <div className="flex items-center gap-2">
                      <span className="font-medium">
                        £{Number(pl.amount).toFixed(2)} {pl.currency}
                      </span>
                      <span
                        className={`rounded-full px-2 py-0.5 text-xs font-semibold uppercase ${
                          pl.status === 'PAID'
                            ? 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-300'
                            : pl.status === 'SENT'
                              ? 'bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-300'
                              : pl.status === 'FAILED'
                                ? 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-300'
                                : pl.status === 'EXPIRED'
                                  ? 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-300'
                                  : 'bg-gray-100 text-gray-800 dark:bg-gray-800 dark:text-gray-300'
                        }`}
                      >
                        {pl.status}
                      </span>
                    </div>
                    {pl.vehicle_name && (
                      <p className="text-muted-foreground text-sm">{pl.vehicle_name}</p>
                    )}
                    <p className="text-muted-foreground text-xs">
                      {new Date(pl.created_at).toLocaleString('en-GB')}
                      {pl.paid_at && ` · Paid ${new Date(pl.paid_at).toLocaleString('en-GB')}`}
                    </p>
                  </div>
                  {pl.payment_link_url && (
                    <Button asChild variant="outline" size="sm">
                      <a href={pl.payment_link_url} target="_blank" rel="noopener noreferrer">
                        <ExternalLink className="mr-1 h-3 w-3" />
                        Link
                      </a>
                    </Button>
                  )}
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* SMS Link Tracking Card */}
      {tracking?.hasTracking && (
        <Card>
          <CardHeader className="pb-2">
            <div className="flex items-center gap-2">
              <MousePointerClick className="text-muted-foreground h-5 w-5" />
              <CardTitle className="text-sm">SMS Link Tracking</CardTitle>
            </div>
            <CardDescription>Track when the customer opens links sent via SMS</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
              {tracking.links.map((link) => (
                <div
                  key={link.id}
                  className="rounded-lg border bg-white p-4 shadow-sm dark:bg-gray-900"
                >
                  <div className="mb-3 flex items-center justify-between">
                    <span className="rounded-full bg-blue-100 px-2 py-0.5 text-xs font-semibold tracking-wider text-blue-800 uppercase dark:bg-blue-900 dark:text-blue-300">
                      {formatSnakeCase(link.linkType)}
                    </span>
                    <span
                      className={`rounded-full px-2 py-0.5 text-[10px] font-bold uppercase ${
                        link.clicked
                          ? 'bg-green-100 text-green-700 dark:bg-green-950 dark:text-green-400'
                          : 'bg-gray-100 text-gray-500 dark:bg-gray-800 dark:text-gray-400'
                      }`}
                    >
                      {link.clicked ? 'Clicked' : 'Pending'}
                    </span>
                  </div>

                  <div className="space-y-2">
                    <div className="group relative">
                      <p className="text-muted-foreground mb-1 text-[10px] font-bold tracking-wider uppercase">
                        Original URL
                      </p>
                      <a
                        href={normalizeVehicleUrl(link.originalUrl)}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-primary flex items-start gap-1 overflow-hidden text-xs break-all hover:underline"
                      >
                        <span className="min-w-0 flex-1">{link.originalUrl}</span>
                        <ExternalLink className="mt-0.5 h-3 w-3 flex-shrink-0" />
                      </a>
                    </div>

                    <div className="flex flex-col gap-1.5 pt-1">
                      {link.clicked && link.clickedAt && (
                        <div className="flex items-center gap-2 text-xs text-green-600 dark:text-green-400">
                          <CheckCircle className="h-3.5 w-3.5" />
                          <span>Clicked {formatDateTime(link.clickedAt)}</span>
                        </div>
                      )}
                      <div className="flex items-center gap-2 text-[10px] text-gray-400">
                        <Clock className="h-3 w-3" />
                        <span>Sent {formatDateTime(link.createdAt)}</span>
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Tool Executions Card */}
      {call.search_queries && call.search_queries.length > 0 && (
        <Card>
          <CardHeader className="pb-2">
            <div className="flex items-center gap-2">
              <Search className="text-muted-foreground h-5 w-5" />
              <CardTitle className="text-sm">Tool Executions</CardTitle>
            </div>
            <CardDescription>
              Tools executed during the call ({call.search_queries.length} total) - Click to view
              results
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              {call.search_queries.map((query: SearchQuery, idx: number) => (
                <div key={query.id}>
                  <button
                    onClick={() =>
                      setExpandedQueryId(expandedQueryId === query.id ? null : query.id)
                    }
                    className="hover:bg-muted/50 w-full rounded-lg border p-4 text-left transition-colors"
                  >
                    <div className="mb-3 flex items-center justify-between">
                      <span className="text-sm font-medium">Execution #{idx + 1}</span>
                      <div className="flex items-center gap-3">
                        <Badge variant="secondary">{query.results_count ?? 0} results</Badge>
                        <span className="text-xs text-gray-500">
                          {new Date(query.created_at).toLocaleTimeString()}
                        </span>
                        {expandedQueryId === query.id ? (
                          <ChevronUp className="text-muted-foreground h-4 w-4" />
                        ) : (
                          <ChevronDown className="text-muted-foreground h-4 w-4" />
                        )}
                      </div>
                    </div>
                    <div className="flex flex-wrap gap-2">
                      {Object.entries(query.executed_query)
                        .filter(
                          ([, value]) => value !== undefined && value !== null && value !== '',
                        )
                        .map(([key, value]) => (
                          <span
                            key={key}
                            className="inline-flex items-center rounded-md bg-white px-2 py-1 text-xs dark:bg-gray-800"
                          >
                            <span className="font-medium text-gray-500 dark:text-gray-400">
                              {key}:
                            </span>
                            <span className="ml-1 break-all text-gray-900 dark:text-gray-100">
                              {typeof value === 'object' ? JSON.stringify(value) : String(value)}
                            </span>
                          </span>
                        ))}
                    </div>
                  </button>

                  {expandedQueryId === query.id && (
                    <div className="mt-2 rounded-lg border bg-white p-4 dark:bg-gray-900">
                      <h4 className="mb-3 text-sm font-semibold text-gray-700 dark:text-gray-300">
                        Results ({query.results_count ?? 0} items)
                      </h4>
                      {query.results ? (
                        <div className="max-h-96 space-y-2 overflow-y-auto">
                          {(() => {
                            const toolName = query.executed_query?.tool_name;
                            const isSearchCars =
                              toolName === 'search_car' || toolName === 'search_cars';
                            const results = query.results as Record<string, unknown>;

                            const items: unknown[] = isSearchCars
                              ? Array.isArray(results)
                                ? results
                                : (results?.vehicles as unknown[]) ||
                                  (results?.data as unknown[]) ||
                                  (results?.items as unknown[]) ||
                                  (results?.results as unknown[]) ||
                                  []
                              : Array.isArray(results)
                                ? results
                                : [results];

                            return items.map((item: unknown, itemIdx: number) => (
                              <div
                                key={itemIdx}
                                className="rounded-md border bg-gray-50 p-3 dark:border-gray-700 dark:bg-gray-800"
                              >
                                <pre className="overflow-x-auto text-xs text-gray-700 dark:text-gray-300">
                                  {JSON.stringify(item, null, 2)}
                                </pre>
                              </div>
                            ));
                          })()}
                        </div>
                      ) : (
                        <p className="text-sm text-gray-500 dark:text-gray-400">
                          No results found for this execution
                        </p>
                      )}
                    </div>
                  )}
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Review Config Dialog */}
      <Dialog open={configDialogOpen} onOpenChange={setConfigDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Select Review Config</DialogTitle>
            <DialogDescription>
              Choose a review configuration to use for this review, or leave empty to use the
              default.
            </DialogDescription>
          </DialogHeader>
          <div className="py-4">
            <select
              className="border-input bg-background w-full rounded-md border px-3 py-2 text-sm"
              value={selectedConfigId}
              onChange={(e) => setSelectedConfigId(e.target.value)}
            >
              <option value="">Use default config</option>
              {reviewConfigs.map((rc) => (
                <option key={rc.id} value={rc.id}>
                  {rc.name}
                  {rc.is_default ? ' (System Default)' : ''}
                </option>
              ))}
            </select>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setConfigDialogOpen(false)}>
              Cancel
            </Button>
            <Button onClick={handleConfirmStartReview} disabled={startReviewMutation.isPending}>
              {startReviewMutation.isPending ? 'Starting...' : 'Start Review'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* API Node Detail Modal */}
      <Dialog open={!!apiNodeDetail} onOpenChange={() => setApiNodeDetail(null)}>
        <DialogContent className="max-h-[80vh] max-w-2xl overflow-auto">
          <DialogHeader>
            <DialogTitle>API Node: {apiNodeDetail?.nodeId}</DialogTitle>
            <DialogDescription>Request payload and response data</DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <h4 className="mb-1.5 text-sm font-medium">Request Payload</h4>
              <pre className="bg-muted max-h-60 overflow-auto rounded-md p-3 text-xs">
                {apiNodeDetail?.request_payload
                  ? JSON.stringify(apiNodeDetail.request_payload, null, 2)
                  : 'No payload'}
              </pre>
            </div>
            <div>
              <h4 className="mb-1.5 text-sm font-medium">Response Data</h4>
              <pre className="bg-muted max-h-60 overflow-auto rounded-md p-3 text-xs">
                {apiNodeDetail?.response_data
                  ? JSON.stringify(apiNodeDetail.response_data, null, 2)
                  : 'No response'}
              </pre>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {featureAgentImprovement && call && (
        <ReplayAsTestDialog call={call} open={replayOpen} onOpenChange={setReplayOpen} />
      )}
    </div>
  );
};
